// LSP client over Phoenix Channel.
//
// Wraps a Phoenix Channel to provide JSON-RPC 2.0 request/response
// correlation and CodeMirror 6 integration (completions, hover, diagnostics).
//
// The server side (lib/operation_web/channels/lsp_channel.ex) forwards events
// "request", "response", and "notification" to the LSP stdio. We distinguish
// on the wire by the event name: client-initiated requests → "request",
// client-initiated notifications → "notification", client responses to
// server-initiated requests → "response". Diagnostics and other server-pushed
// messages are routed to notification handlers below.

import type { Socket } from "phoenix";
import type {
  Completion,
  CompletionContext,
  CompletionResult,
} from "@codemirror/autocomplete";
import type { Diagnostic } from "@codemirror/lint";
import type { EditorView, Tooltip } from "@codemirror/view";

// ---------------------------------------------------------------------------
// JSON-RPC types
// ---------------------------------------------------------------------------

export interface JsonRpcRequest {
  jsonrpc: "2.0";
  id: number;
  method: string;
  params?: unknown;
}

export interface JsonRpcNotification {
  jsonrpc: "2.0";
  method: string;
  params?: unknown;
}

export interface JsonRpcResponse {
  jsonrpc: "2.0";
  id: number;
  result?: unknown;
  error?: { code: number; message: string; data?: unknown };
}

// JSON-RPC carries structured error info ({code, message, data}) but the
// JS Promise reject contract wants an Error. Wrap as a real Error subclass so
// `@typescript-eslint/prefer-promise-reject-errors` is satisfied AND callers
// can still pull `.code` / `.data` off the rejection.
export class JsonRpcError extends Error {
  code: number;
  data?: unknown;
  constructor(code: number, message: string, data?: unknown) {
    super(message);
    this.name = "JsonRpcError";
    this.code = code;
    this.data = data;
  }
}

type PendingRequest = {
  resolve: (result: unknown) => void;
  reject: (err: JsonRpcError) => void;
  cancel: () => void;
};

export type ServerRequestHandler = (
  method: string,
  params: unknown,
) => Promise<unknown>;

// ---------------------------------------------------------------------------
// LSP client state
// ---------------------------------------------------------------------------

export interface LSPClient {
  readonly onNotification: (
    handler: (n: JsonRpcNotification) => void,
  ) => () => void;
  readonly onServerRequest: (handler: ServerRequestHandler) => () => void;
  readonly request: (method: string, params?: unknown) => Promise<unknown>;
  readonly notify: (method: string, params?: unknown) => void;
  readonly disconnect: () => void;
}

export interface LSPClientOptions {
  requestTimeoutMs?: number;
}

const DEFAULT_REQUEST_TIMEOUT_MS = 10_000;

export function connectLSP(
  socket: Socket,
  sessionId: string,
  language: string,
  opts: LSPClientOptions = {},
): Promise<LSPClient> {
  const topic = `lsp:${sessionId}:${language}`;
  const channel = socket.channel(topic, {});
  const timeoutMs = opts.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;

  const pending = new Map<number, PendingRequest>();
  const notificationHandlers = new Set<(n: JsonRpcNotification) => void>();
  const serverRequestHandlers = new Set<ServerRequestHandler>();
  let nextId = 1;

  const responseRef = channel.on("response", (payload: unknown) => {
    const msg = payload as JsonRpcResponse;
    if (typeof msg.id !== "number") return;
    const pend = pending.get(msg.id);
    if (!pend) return;
    pending.delete(msg.id);
    pend.cancel();
    if (msg.error)
      pend.reject(
        new JsonRpcError(msg.error.code, msg.error.message, msg.error.data),
      );
    else pend.resolve(msg.result);
  });

  const notificationRef = channel.on("notification", (payload: unknown) => {
    const msg = payload as JsonRpcNotification;
    for (const h of notificationHandlers) h(msg);
  });

  // Phoenix `channel.on` callbacks must return void; an `async` arrow returns
  // a Promise which violates `@typescript-eslint/no-misused-promises`. Wrap
  // the await in a self-invoked async IIFE so the outer callback is sync.
  const requestRef = channel.on("request", (payload: unknown) => {
    const msg = payload as JsonRpcRequest;
    if (typeof msg.id !== "number" || typeof msg.method !== "string") return;

    void (async () => {
      // Try registered handlers in order; first resolving one wins.
      let result: unknown = null;
      let error: { code: number; message: string } | undefined;

      for (const h of serverRequestHandlers) {
        try {
          result = await h(msg.method, msg.params);
          error = undefined;
          break;
        } catch (e) {
          error = {
            code: -32603,
            message: e instanceof Error ? e.message : String(e),
          };
        }
      }

      const response: JsonRpcResponse = error
        ? { jsonrpc: "2.0", id: msg.id, error }
        : { jsonrpc: "2.0", id: msg.id, result };
      channel.push("response", response);
    })();
  });

  return new Promise((resolve, reject) => {
    channel
      .join()
      .receive("ok", () => {
        resolve({
          onNotification(handler) {
            notificationHandlers.add(handler);
            return () => notificationHandlers.delete(handler);
          },

          onServerRequest(handler) {
            serverRequestHandlers.add(handler);
            return () => serverRequestHandlers.delete(handler);
          },

          request(method, params) {
            const id = nextId++;
            const req: JsonRpcRequest = {
              jsonrpc: "2.0",
              id,
              method,
              ...(params !== undefined ? { params } : {}),
            };

            return new Promise((res, rej) => {
              const timer = setTimeout(() => {
                pending.delete(id);
                rej(new JsonRpcError(-32000, `timeout: ${method}`));
              }, timeoutMs);

              pending.set(id, {
                resolve: res,
                reject: rej,
                cancel: () => {
                  clearTimeout(timer);
                },
              });
              channel.push("request", req);
            });
          },

          notify(method, params) {
            const msg: JsonRpcNotification = {
              jsonrpc: "2.0",
              method,
              ...(params !== undefined ? { params } : {}),
            };
            channel.push("notification", msg);
          },

          disconnect() {
            for (const pend of pending.values()) {
              pend.cancel();
              pend.reject(new JsonRpcError(-32001, "disconnected"));
            }
            pending.clear();
            notificationHandlers.clear();
            serverRequestHandlers.clear();
            channel.off("response", responseRef);
            channel.off("notification", notificationRef);
            channel.off("request", requestRef);
            channel.leave();
          },
        });
      })
      .receive("error", (err: unknown) => {
        reject(
          err instanceof Error
            ? err
            : new JsonRpcError(-32000, "join error", err),
        );
      })
      .receive("timeout", () => {
        reject(new JsonRpcError(-32000, "join timeout"));
      });
  });
}

// ---------------------------------------------------------------------------
// LSP Position conversion
// ---------------------------------------------------------------------------

export function posToLspPosition(
  view: EditorView,
  pos: number,
): { line: number; character: number } {
  const line = view.state.doc.lineAt(pos);
  return { line: line.number - 1, character: pos - line.from };
}

export function lspPositionToPos(
  view: EditorView,
  lspPos: { line: number; character: number },
): number {
  const doc = view.state.doc;
  const lineNum = Math.max(1, Math.min(lspPos.line + 1, doc.lines));
  const line = doc.line(lineNum);
  return Math.min(line.from + lspPos.character, line.to);
}

// ---------------------------------------------------------------------------
// LSP completion → CodeMirror completion
// ---------------------------------------------------------------------------

interface LspCompletionItem {
  label: string;
  detail?: string;
  kind?: number;
  insertText?: string;
  documentation?: string | { kind: string; value: string };
}

interface LspCompletionList {
  isIncomplete?: boolean;
  items: LspCompletionItem[];
}

// LSP 3.17 CompletionItemKind (1-25) → CodeMirror completion type tag.
// CM types are informational hints for styling; multiple LSP kinds fold into
// the same CM type when CM has no direct equivalent.
const kindMap: Record<number, string> = {
  1: "text", // Text
  2: "method", // Method
  3: "function", // Function
  4: "method", // Constructor — no CM "constructor" icon; method is closest
  5: "variable", // Field
  6: "variable", // Variable
  7: "class", // Class
  8: "interface", // Interface
  9: "namespace", // Module
  10: "property", // Property
  11: "type", // Unit
  12: "constant", // Value
  13: "enum", // Enum
  14: "keyword", // Keyword
  15: "text", // Snippet
  16: "constant", // Color
  17: "text", // File
  18: "text", // Reference
  19: "text", // Folder
  20: "constant", // EnumMember
  21: "constant", // Constant
  22: "class", // Struct
  23: "keyword", // Event
  24: "keyword", // Operator
  25: "type", // TypeParameter
};

export function makeLspCompletionSource(
  client: LSPClient,
  uri: string,
): (ctx: CompletionContext) => Promise<CompletionResult | null> {
  return async (ctx: CompletionContext): Promise<CompletionResult | null> => {
    if (!ctx.view) return null;
    const pos = posToLspPosition(ctx.view, ctx.pos);

    let result: LspCompletionList | LspCompletionItem[] | null;
    try {
      result = (await client.request("textDocument/completion", {
        textDocument: { uri },
        position: pos,
      })) as LspCompletionList | LspCompletionItem[] | null;
    } catch (err) {
      console.warn("LSP completion failed:", err);
      return null;
    }

    // User may have typed more since the request fired; CM 6 sets ctx.aborted.
    if (ctx.aborted) return null;
    if (!result) return null;

    const items = Array.isArray(result) ? result : result.items;
    if (items.length === 0) return null;

    const completions: Completion[] = items.map((item) => {
      const c: Completion = {
        label: item.label,
        apply: item.insertText ?? item.label,
      };
      if (item.detail !== undefined) c.detail = item.detail;
      if (item.kind !== undefined) {
        const type = kindMap[item.kind];
        if (type !== undefined) c.type = type;
      }
      const info =
        typeof item.documentation === "string"
          ? item.documentation
          : item.documentation?.value;
      if (info !== undefined) c.info = info;
      return c;
    });

    const word = ctx.matchBefore(/\w*/);
    const from = word?.from ?? ctx.pos;

    return { from, options: completions };
  };
}

// ---------------------------------------------------------------------------
// LSP hover → CodeMirror tooltip
// ---------------------------------------------------------------------------

interface LspHoverContent {
  contents:
    | string
    | { kind: string; value: string }
    | Array<string | { language: string; value: string }>;
}

export function hoverContentToText(
  contents: LspHoverContent["contents"],
): string {
  if (typeof contents === "string") return contents;
  if (Array.isArray(contents)) {
    return contents
      .map((c) => (typeof c === "string" ? c : c.value))
      .join("\n\n");
  }
  return contents.value;
}

export function makeLspHover(
  client: LSPClient,
  uri: string,
): (view: EditorView, pos: number) => Promise<Tooltip | null> {
  return async (view: EditorView, pos: number): Promise<Tooltip | null> => {
    const lspPos = posToLspPosition(view, pos);

    let result: LspHoverContent | null;
    try {
      result = (await client.request("textDocument/hover", {
        textDocument: { uri },
        position: lspPos,
      })) as LspHoverContent | null;
    } catch (err) {
      console.warn("LSP hover failed:", err);
      return null;
    }

    if (!result || !result.contents) return null;

    const text = hoverContentToText(result.contents);
    if (!text.trim()) return null;

    return {
      pos,
      create: () => {
        const dom = document.createElement("div");
        dom.className = "cm-lsp-hover-tooltip";
        dom.textContent = text;
        return { dom };
      },
    };
  };
}

// ---------------------------------------------------------------------------
// LSP diagnostics → CodeMirror diagnostics
// ---------------------------------------------------------------------------

interface LspDiagnostic {
  range: {
    start: { line: number; character: number };
    end: { line: number; character: number };
  };
  severity?: number;
  message: string;
  source?: string;
}

const severityMap: Record<number, Diagnostic["severity"]> = {
  1: "error",
  2: "warning",
  3: "info",
  4: "hint",
};

export function lspDiagnosticsToCm(
  view: EditorView,
  diags: LspDiagnostic[],
): Diagnostic[] {
  return diags.map((d) => {
    const cm: Diagnostic = {
      from: lspPositionToPos(view, d.range.start),
      to: lspPositionToPos(view, d.range.end),
      severity:
        d.severity !== undefined && severityMap[d.severity]
          ? severityMap[d.severity]!
          : "error",
      message: d.message,
    };
    if (d.source !== undefined) cm.source = d.source;
    return cm;
  });
}

export type { LspDiagnostic };
